export interface SignalRequest {
  symbol: string;
  timeframe: string;
  signal_type: "BUY" | "SELL";
  price: number;
  sl?: number;
  tp?: number;
  spread?: number;
  balance?: number;
  setup_name?: string;
  strategy_id?: string;
}

export interface BrokerConfigInput {
  login: string;
  password: string;
  server: string;
  setup_url?: string;
}

export interface BrokerStatus {
  configured: boolean;
  server?: string;
  account_id?: string;
  setup_url?: string;
  configured_at?: string;
  last_applied_at?: string;
}

const URL_KEY = "gs.webhook.url";
const TOKEN_KEY = "gs.webhook.token";
const ADMIN_KEY = "gs.adminToken";

export interface WebhookConfig {
  url: string;
  token: string;
  adminToken: string;
}

const DEFAULTS: WebhookConfig = {
  url: "https://goldscope-jev-worker.seddikllaa06.workers.dev/signal",
  token: "dc81e3e1bcec35b6e38d7b340f1d5c5862338aefd127cc32",
  adminToken: "",
};

export function getWebhookConfig(): WebhookConfig {
  return {
    url: localStorage.getItem(URL_KEY) ?? DEFAULTS.url,
    token: localStorage.getItem(TOKEN_KEY) ?? DEFAULTS.token,
    adminToken: localStorage.getItem(ADMIN_KEY) ?? DEFAULTS.adminToken,
  };
}

export function setWebhookConfig(config: WebhookConfig): void {
  localStorage.setItem(URL_KEY, config.url);
  localStorage.setItem(TOKEN_KEY, config.token);
  localStorage.setItem(ADMIN_KEY, config.adminToken);
}

export interface DispatchResult {
  ok: boolean;
  status: number;
  body: Record<string, unknown>;
}

/** Derives the Worker base URL from a /signal endpoint URL. */
export function workerBaseUrl(url: string): string {
  return url.replace(/\/signal$/, "") || url;
}

async function post(url: string, body: unknown, headers: Record<string, string> = {}) : Promise<DispatchResult>{
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify(body),
    });
    const parsed = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    return { ok: res.ok, status: res.status, body: parsed };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      body: { status: "NETWORK_ERROR", error: err instanceof Error ? err.message : "fetch failed" },
    };
  }
}

async function get(url: string, headers: Record<string, string> = {}): Promise<DispatchResult> {
  try {
    const res = await fetch(url, { headers });
    const parsed = (await res.json().catch(() => ({}))) as Record<string, unknown>;
    return { ok: res.ok, status: res.status, body: parsed };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      body: { status: "NETWORK_ERROR", error: err instanceof Error ? err.message : "fetch failed" },
    };
  }
}

/** Stage 1 → Stage 2 webhook dispatch (X-Strategy-Token auth). */
export function dispatchSignal(signal: SignalRequest): Promise<DispatchResult> {
  const { url, token } = getWebhookConfig();
  return post(url, signal, { "X-Strategy-Token": token });
}

/** Saves broker credentials via the JEV AI Worker (dashboard → MT5). */
export function saveBrokerConfig(input: BrokerConfigInput): Promise<DispatchResult> {
  const { url, adminToken } = getWebhookConfig();
  return post(`${workerBaseUrl(url)}/config/broker`, input, { Authorization: `Bearer ${adminToken}` });
}

/** Reads current broker provisioning status (never returns the password). */
export async function getBrokerStatus(): Promise<BrokerStatus> {
  const { url, adminToken } = getWebhookConfig();
  const res = await get(`${workerBaseUrl(url)}/config/broker`, { Authorization: `Bearer ${adminToken}` });
  const broker = (res.body.broker ?? {}) as Record<string, unknown>;
  if (res.status === 200 && res.body.status === "OK") {
    return {
      configured: true,
      server: typeof broker.server === "string" ? broker.server : undefined,
      account_id: typeof broker.account_id === "string" ? broker.account_id : undefined,
      setup_url: typeof broker.setup_url === "string" ? broker.setup_url : undefined,
      configured_at: typeof broker.configured_at === "string" ? broker.configured_at : undefined,
      last_applied_at: typeof broker.last_applied_at === "string" ? broker.last_applied_at : undefined,
    };
  }
  return { configured: false };
}